import React, { useState, useRef, useEffect } from 'react';
import { X, Plus, Check } from 'lucide-react';

const TagSection = ({ tags, onTagsChange, disabled, onAddingStateChange }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState('');
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const listEndRef = useRef(null);

  // Notify parent of adding state
  useEffect(() => {
    onAddingStateChange?.(isAdding);
  }, [isAdding, onAddingStateChange]);

  // Focus input when adding
  useEffect(() => {
    if (isAdding) setTimeout(() => inputRef.current?.focus(), 50);
  }, [isAdding]);

  // Click outside handler
  useEffect(() => {
    if (!isAdding) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsAdding(false);
        setNewValue('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAdding]);

  const handleConfirmAdd = () => {
    const val = newValue.trim();
    if (val && !tags.includes(val)) {
      onTagsChange([...tags, val]);
      setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
    setNewValue('');
    setIsAdding(false);
  };

  const handleRemove = (idx) => {
    onTagsChange(tags.filter((_, i) => i !== idx));
  };

  return (
    <div>
      {/* Header: label + button */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <label className="text-sm font-medium text-charcoal">Tags</label>
          <p className="text-xs text-gray-soft">{tags.length} added</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => { setIsAdding(true); setNewValue(''); }}
            disabled={isAdding || disabled}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-sage text-sage rounded-lg hover:bg-sage hover:text-white transition-colors disabled:opacity-50 whitespace-nowrap"
            data-testid="add-tag-btn"
          >
            <Plus size={14} />
            Add
          </button>
        </div>
      </div>

      {/* Inline add text box — above the tags list */}
      {isAdding && (
        <div ref={wrapperRef} className="flex items-center gap-2 mb-3" data-testid="inline-tag-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleConfirmAdd(); }
              if (e.key === 'Escape') { setIsAdding(false); setNewValue(''); }
            }}
            placeholder="Type a tag..."
            className="flex-1 px-3 py-2.5 border border-sage rounded-lg text-sm text-charcoal placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-sage/30"
            data-testid="new-tag-input"
          />
          <button
            type="button"
            onClick={handleConfirmAdd}
            disabled={!newValue.trim()}
            className="p-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="confirm-new-tag-btn"
          >
            <Check size={16} />
          </button>
        </div>
      )}

      {/* Tag pills */}
      {tags.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, idx) => (
            <span
              key={`${tag}-${idx}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sage-light text-charcoal rounded-full text-sm group"
              data-testid={`tag-pill-${idx}`}
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="text-gray-300 hover:text-red-500 transition-colors"
                data-testid={`remove-tag-${idx}`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
          <div ref={listEndRef} />
        </div>
      ) : (
        <p className="text-sm text-gray-soft italic">No tags added yet.</p>
      )}
    </div>
  );
};

export default TagSection;
